import { Popover } from '@headlessui/react';
import { Menu, X } from '@/components/icons/lucide';
import { Href, Link, usePathname, useRouter } from 'expo-router';
import { clsx } from 'clsx';
import React, { useState, useCallback } from 'react';

import { Container } from '../container';

import { LogoHomeSvg } from './logo-home';
import { SignInButton } from './sign-in-button';

import { useScrollCounter } from '@/hooks/use-scroll-counter';
import { useUser } from '@/states/auth';
import { useSignInModal } from '@/providers/modal/states';

const navItems = [
  {
    name: 'Articles',
    href: '/articles',
  },
  {
    name: 'Demos',
    href: '/demos',
  },
  {
    name: 'Course',
    href: 'https://reanimate.dev',
    external: true,
  },
] as const;

const Header: React.FC = React.memo(() => {
  const pathname = usePathname();
  const router = useRouter();
  const user = useUser();
  const signInModal = useSignInModal();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const hasReachedOffset = useScrollCounter(10);

  const toggleMobileMenu = useCallback(() => {
    setMobileMenuOpen((prev) => !prev);
  }, []);

  const closeMobileMenu = useCallback(() => {
    setMobileMenuOpen(false);
  }, []);

  const handleProfileClick = useCallback(() => {
    if (!user?.email) {
      signInModal.open();
    } else {
      router.push('/profile');
    }
    closeMobileMenu();
  }, [user?.email, signInModal, router, closeMobileMenu]);

  return (
    <div className="mb-20">
      {/* Desktop Header */}
      <Popover
        className={clsx(
          hasReachedOffset
            ? 'border-b-white/5 bg-background'
            : 'border-b-transparent bg-transparent',
          'hidden md:block fixed top-0 left-0 right-0 z-50 border-b duration-300 transition-all'
        )}
      >
        <Container size="md">
          <div
            className={clsx(
              hasReachedOffset ? 'py-3 sm:py-4' : 'py-4 sm:py-6',
              'flex justify-between items-center md:justify-start transition-all duration-500'
            )}
          >
            <div>
              <Link href="/" className="flex mt-1.5">
                <div style={{ width: 190, height: 35 }} className="flex">
                  <LogoHomeSvg />
                </div>
              </Link>
            </div>
            <div className="hidden md:flex-1 md:flex md:items-center md:justify-between">
              <nav className="flex space-x-7 ml-auto mt-1.5 items-center">
                {navItems.map((item, index) => {
                  const { name, href } = item;
                  const isExternal = 'external' in item && item.external;
                  if (isExternal) {
                    return (
                      <a
                        key={index.toString()}
                        href={href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="whitespace-nowrap group text-sm font-medium hover:text-white tracking-wide transition-colors text-subtle"
                      >
                        {name}
                        <div className="h-[1.5px] rounded-sm bg-subtle-300 transition-all duration-500 ease-in-out group-hover:w-full w-0" />
                      </a>
                    );
                  }
                  return (
                    <Link
                      key={index.toString()}
                      href={href as Href}
                      selectable={false}
                      className={clsx(
                        'whitespace-nowrap group text-sm font-medium hover:text-white tracking-wide transition-colors',
                        pathname === href ? 'text-subtle-300' : 'text-subtle'
                      )}
                    >
                      {name}
                      <div
                        className={clsx(
                          'h-[1.5px] rounded-sm bg-subtle-300 transition-all duration-500 ease-in-out group-hover:w-full',
                          pathname === href ? 'w-full' : 'w-0'
                        )}
                      />
                    </Link>
                  );
                })}
                <SignInButton />
              </nav>
            </div>
          </div>
        </Container>
      </Popover>

      {/* Mobile Header */}
      <div
        className={clsx(
          hasReachedOffset || mobileMenuOpen
            ? 'border-b-white/5 bg-background'
            : 'border-b-transparent bg-transparent',
          'md:hidden fixed top-0 left-0 right-0 z-50 border-b duration-300 transition-all'
        )}
      >
        <Container size="md">
          <div className="flex justify-between items-center py-4">
            <Link href="/" className="flex" onPress={closeMobileMenu}>
              <div style={{ width: 150, height: 28 }} className="flex">
                <LogoHomeSvg />
              </div>
            </Link>
            <button
              onClick={toggleMobileMenu}
              className="p-2 text-subtle hover:text-white transition-colors"
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </Container>
      </div>

      {/* Mobile Menu Overlay */}
      <div
        className={clsx(
          'md:hidden fixed inset-0 z-40 transition-all duration-300',
          mobileMenuOpen
            ? 'opacity-100 pointer-events-auto'
            : 'opacity-0 pointer-events-none'
        )}
      >
        <div
          className="absolute inset-0 bg-black/60"
          onClick={closeMobileMenu}
        />
        <div
          className={clsx(
            'absolute top-[60px] left-0 right-0 bg-background border-b border-white/5 transition-all duration-300',
            mobileMenuOpen
              ? 'translate-y-0 opacity-100'
              : '-translate-y-4 opacity-0'
          )}
        >
          <Container size="md">
            <nav className="pt-10 pb-4 space-y-1">
              <Link
                href="/"
                onPress={closeMobileMenu}
                className={clsx(
                  'block py-3 px-4 rounded-lg text-base font-medium transition-colors',
                  pathname === '/'
                    ? 'text-white bg-white/10'
                    : 'text-subtle hover:text-white hover:bg-white/5'
                )}
              >
                Home
              </Link>
              {navItems.map((item, index) => {
                const { name, href } = item;
                const isExternal = 'external' in item && item.external;
                if (isExternal) {
                  return (
                    <a
                      key={index.toString()}
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={closeMobileMenu}
                      className="block py-3 px-4 rounded-lg text-base font-medium text-subtle hover:text-white hover:bg-white/5 transition-colors"
                    >
                      {name}
                    </a>
                  );
                }
                return (
                  <Link
                    key={index.toString()}
                    href={href as Href}
                    onPress={closeMobileMenu}
                    className={clsx(
                      'block py-3 px-4 rounded-lg text-base font-medium transition-colors',
                      pathname === href
                        ? 'text-white bg-white/10'
                        : 'text-subtle hover:text-white hover:bg-white/5'
                    )}
                  >
                    {name}
                  </Link>
                );
              })}
              <div className="pt-2 border-t border-white/10 mt-2">
                <button
                  onClick={handleProfileClick}
                  className={clsx(
                    'w-full text-left py-3 px-4 rounded-lg text-base font-medium transition-colors',
                    pathname === '/profile'
                      ? 'text-white bg-white/10'
                      : 'text-subtle hover:text-white hover:bg-white/5'
                  )}
                >
                  {user?.email ? 'Profile' : 'Sign In'}
                </button>
              </div>
            </nav>
          </Container>
        </div>
      </div>
    </div>
  );
});

export { Header };
